import { useCallback, useEffect, useState } from "react";
import { Loader2, ScanLine, Upload, CheckCircle2, RotateCcw } from "lucide-react";
import { AppShell } from "@/components/Layout";
import { KPI, MetricRow, Overline, StatusPill } from "@/components/Bits";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

export default function LabPortal() {
  const { user } = useAuth();
  const { t, ts, tm } = useI18n();
  const [tab, setTab] = useState("queue");
  const [samples, setSamples] = useState([]);
  const [counts, setCounts] = useState({});
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [defs, setDefs] = useState([]);
  const [values, setValues] = useState({});
  const [comments, setComments] = useState({});
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);

  const isReviewer = user?.role === "lab_reviewer" || user?.role === "admin";
  const isTech = user?.role === "lab_technician" || user?.role === "admin";

  const load = useCallback(async (q = "") => {
    try {
      const { data } = await api.get("/lab/samples", { params: q ? { q } : {} });
      setSamples(data.samples);
      setCounts(data.counts);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, e.message));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (sampleId) => {
    try {
      const { data } = await api.get(`/lab/samples/${sampleId}`);
      setSelected(data.sample);
      setDefs(data.metric_definitions);
      setValues(data.sample.results?.values || {});
      setComments(data.sample.results?.comments || {});
      setTab("sample");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, e.message));
    }
  };

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load(query);
      if (selected) await open(selected.sample_id);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, e.message));
    } finally {
      setBusy(false);
    }
  };

  const receive = (id) => act(async () => {
    await api.post(`/lab/samples/${id}/receive`);
    toast.success(t("lab.received"));
  });

  const submitResults = (e) => {
    e.preventDefault();
    return act(async () => {
      await api.post(`/lab/samples/${selected.sample_id}/results`, { values, comments });
      toast.success(t("lab.submitted"));
    });
  };

  const importCsv = () => act(async () => {
    const { data } = await api.post(`/lab/samples/${selected.sample_id}/results/csv`, { csv });
    setValues(data.sample.results.values);
    toast.success(t("lab.imported"));
  });

  const review = (decision) => act(async () => {
    const { data } = await api.post(`/lab/samples/${selected.sample_id}/review`, { decision, note: "" });
    toast.success(decision === "approve" ? `${t("lab.validate")} — score ${data.report.score}/100` : t("lab.rejected"));
  });

  const nav = [
    { key: "queue", label: t("lab.samples") },
    ...(selected ? [{ key: "sample", label: selected.sample_id }] : []),
  ];

  return (
    <AppShell title={t("lab.title")} nav={nav} active={tab} onNavigate={setTab} testId="lab-shell">
      {tab === "queue" && (
        <div data-testid="lab-queue">
          <div className="mb-6 flex flex-col gap-4 border-b border-black/10 pb-6 md:flex-row md:items-end md:justify-between">
            <div>
              <Overline>{t("lab.title")}</Overline>
              <h1 className="mt-3 font-display text-4xl font-light tracking-tighter">{t("lab.samples")}</h1>
              <p className="mt-2 font-mono-dt text-xs text-zinc-400">{user?.role} · {t("lab.pseudonymised")}</p>
            </div>
            <div className="flex items-center gap-3">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("lab.search")} data-testid="lab-search"
                className="w-56 border-b border-black/20 bg-transparent pb-2 text-sm outline-none focus:border-black" />
              <button onClick={() => load(query)} data-testid="lab-search-btn" className="rounded-full border border-black/20 px-4 py-2 text-[12px] font-semibold hover:bg-zinc-50">
                <ScanLine className="mr-1 inline h-3.5 w-3.5" /> {t("lab.searchBtn")}
              </button>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPI label={t("lab.assigned")} value={samples.length} testId="lab-kpi-total" />
            <KPI label={t("lab.awaiting")} value={(counts.kit_shipped || 0) + (counts.kit_delivered || 0) + (counts.sample_awaiting_return || 0)} testId="lab-kpi-await" />
            <KPI label={t("lab.inAnalysis")} value={(counts.sample_received_lab || 0) + (counts.analysis_in_progress || 0)} testId="lab-kpi-analysis" />
            <KPI label={t("lab.awaitingValidation")} value={counts.results_validation || 0} testId="lab-kpi-validation" />
          </div>
          <div className="mt-6 overflow-x-auto border border-black/10 bg-white">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-black/10 text-left">
                  {["Sample ID", t("lab.analysis"), t("lab.status"), t("lab.parameters"), t("lab.receivedAt"), ""].map((h) => (
                    <th key={h} className="px-4 py-3 overline text-[9px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {samples.map((s) => (
                  <tr key={s.sample_id} className="border-b border-black/5 hover:bg-[#F8F9FA]" data-testid={`lab-row-${s.sample_id}`}>
                    <td className="px-4 py-3 font-mono-dt text-xs">{s.sample_id}</td>
                    <td className="px-4 py-3">{s.analysis_name}</td>
                    <td className="px-4 py-3"><StatusPill>{ts(s.status)}</StatusPill></td>
                    <td className="px-4 py-3 font-mono-dt text-xs text-zinc-500">{s.metrics_required.length}</td>
                    <td className="px-4 py-3 font-mono-dt text-[10px] text-zinc-400">{s.received_at ? new Date(s.received_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => open(s.sample_id)} data-testid={`lab-open-${s.sample_id}`} className="text-xs font-semibold underline underline-offset-4">{t("lab.open")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "sample" && selected && (
        <div className="grid gap-6 lg:grid-cols-12" data-testid="lab-sample">
          <div className="lg:col-span-4">
            <div className="border border-black/10 bg-white p-6">
              <Overline>{t("lab.sample")}</Overline>
              <p className="mt-3 font-mono-dt text-lg">{selected.sample_id}</p>
              <p className="mt-2 text-sm text-zinc-600">{selected.analysis_name}</p>
              <div className="mt-4"><StatusPill>{ts(selected.status)}</StatusPill></div>
              <p className="mt-5 font-mono-dt text-[10px] text-zinc-400">
                {t("lab.receivedAt")} {selected.received_at ? new Date(selected.received_at).toLocaleString() : "—"}
              </p>
              {selected.step_index === undefined && null}
              {["kit_preparation", "kit_shipped", "kit_delivered", "sample_awaiting_return"].includes(selected.status) && (
                <button onClick={() => receive(selected.sample_id)} disabled={busy} data-testid="lab-receive-btn"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-black px-5 py-3 text-[12px] font-semibold text-white disabled:opacity-60">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanLine className="h-3.5 w-3.5" />} {t("lab.receive")}
                </button>
              )}
            </div>
            <div className="mt-6 border border-black/10 bg-white p-6">
              <Overline>{t("lab.context")}</Overline>
              <div className="mt-4 grid gap-2">
                {Object.entries(selected.declared_context || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-black/5 py-1.5 text-xs">
                    <span className="text-zinc-500">{k}</span><span>{v}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-zinc-400">{t("lab.pseudonymised")} — {t("lab.noleak")}</p>
            </div>
            {selected.review && (
              <div className="mt-6 border border-black/10 bg-white p-6">
                <Overline>{t("lab.review")}</Overline>
                <p className="mt-3 text-sm">{selected.review.decision} · {selected.review.reviewer_email}</p>
                <p className="mt-1 font-mono-dt text-[10px] text-zinc-400">{new Date(selected.review.validated_at).toLocaleString()}</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-8">
            {isTech && (
              <form onSubmit={submitResults} className="border border-black/10 bg-white p-6 md:p-8" data-testid="lab-results-form">
                <Overline>{t("lab.enterresults")}</Overline>
                <div className="mt-6 grid gap-5">
                  {defs.map((d) => (
                    <div key={d.key} className="grid gap-2 sm:grid-cols-[1fr_140px_1fr] sm:items-end">
                      <div>
                        <p className="text-sm font-semibold">{(tm(d.key) || [d.label])[0]}</p>
                        <p className="font-mono-dt text-[10px] text-zinc-400">
                          {d.unit} · {t("lab.reference")} {d.optimal[0]}–{d.optimal[1]} · {d.direction === "up" ? t("lab.higherBetter") : t("lab.lowerBetter")}
                        </p>
                      </div>
                      <input required type="number" step="0.1" data-testid={`lab-value-${d.key}`}
                        value={values[d.key] ?? ""} onChange={(e) => setValues({ ...values, [d.key]: e.target.value })}
                        className="border-b border-black/20 bg-transparent pb-1.5 font-mono-dt text-sm outline-none focus:border-black" />
                      <input placeholder={t("lab.comment")} data-testid={`lab-comment-${d.key}`}
                        value={comments[d.key] ?? ""} onChange={(e) => setComments({ ...comments, [d.key]: e.target.value })}
                        className="border-b border-black/10 bg-transparent pb-1.5 text-xs outline-none focus:border-black" />
                    </div>
                  ))}
                </div>
                <button type="submit" disabled={busy} data-testid="lab-submit-results-btn"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-black px-6 py-3 text-[13px] font-semibold text-white disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("lab.submit")}
                </button>
              </form>
            )}

            {isTech && (
              <div className="mt-6 border border-black/10 bg-white p-6">
                <Overline>{t("lab.importcsv")}</Overline>
                <textarea rows={4} value={csv} onChange={(e) => setCsv(e.target.value)} data-testid="lab-csv-input"
                  placeholder={"metric,value,comment\ncuticle_integrity,72,Moderate alteration\nporosity,58,"}
                  className="mt-3 w-full resize-none border border-black/10 bg-[#F8F9FA] p-3 font-mono-dt text-[11px] outline-none focus:border-black" />
                <button onClick={importCsv} disabled={busy || !csv.trim()} data-testid="lab-csv-btn"
                  className="mt-3 inline-flex items-center gap-2 rounded-full border border-black/20 px-5 py-2.5 text-[12px] font-semibold hover:bg-zinc-50 disabled:opacity-50">
                  <Upload className="h-3.5 w-3.5" /> {t("lab.importcsv")}
                </button>
              </div>
            )}

            {selected.results && (
              <div className="mt-6 border border-black/10 bg-white p-6 md:p-8" data-testid="lab-review-block">
                <Overline>{t("lab.submittedValues")}</Overline>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {Object.entries(selected.results.values).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-black/5 py-1.5 text-xs">
                      <span className="text-zinc-500">{k}</span><span className="font-mono-dt">{v}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 font-mono-dt text-[10px] text-zinc-400">
                  {t("lab.technician")} {selected.results.technician_email} · {new Date(selected.results.submitted_at).toLocaleString()}
                </p>
                {isReviewer && selected.status === "results_validation" && (
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button onClick={() => review("approve")} disabled={busy} data-testid="lab-validate-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-black px-6 py-3 text-[13px] font-semibold text-white disabled:opacity-60">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("lab.validate")}
                    </button>
                    <button onClick={() => review("reject")} disabled={busy} data-testid="lab-reject-btn"
                      className="inline-flex items-center gap-2 rounded-full border border-black/20 px-6 py-3 text-[13px] font-semibold hover:bg-zinc-50 disabled:opacity-60">
                      <RotateCcw className="h-4 w-4" /> {t("lab.reject")}
                    </button>
                  </div>
                )}
                {!isReviewer && selected.status === "results_validation" && (
                  <p className="mt-5 text-xs text-zinc-500">{t("lab.awaitReviewer")}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
